import React from 'react';
import { Shield, Calendar, Building2, Clock, AlertTriangle, Star, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const CLEARANCE_LABELS = {
  level_1: 'Community Volunteer',
  level_2: 'Youth Volunteer',
  level_3: 'Vulnerable Population',
};

const CLEARANCE_COLORS = {
  level_1: { bg: 'bg-slate-100', text: 'text-slate-700', accent: 'bg-slate-600' },
  level_2: { bg: 'bg-blue-50', text: 'text-blue-700', accent: 'bg-blue-600' },
  level_3: { bg: 'bg-purple-50', text: 'text-purple-700', accent: 'bg-purple-600' },
};

const LEVEL_LABELS = {
  level_1: 'General Volunteer',
  level_2: 'Approved Volunteer',
  level_3: 'Trusted Volunteer',
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'text-gray-500 bg-gray-100', icon: null },
  in_progress: { label: 'In Progress', color: 'text-blue-600 bg-blue-50 border border-blue-200', icon: Loader2 },
  verified: { label: 'Verified', color: 'text-emerald-700 bg-emerald-50 border border-emerald-200', icon: CheckCircle },
  expired: { label: 'Expired', color: 'text-red-600 bg-red-50 border border-red-200', icon: XCircle },
  under_review: { label: 'Under Review', color: 'text-amber-600 bg-amber-50 border border-amber-200', icon: AlertTriangle },
  suspended: { label: 'Suspended', color: 'text-red-700 bg-red-100 border border-red-300', icon: XCircle },
  revoked: { label: 'Revoked', color: 'text-red-800 bg-red-200 border border-red-400', icon: XCircle },
};

const CHECK_TYPE_LABELS = {
  nh_criminal: 'NH Criminal History',
  national_criminal: 'National Criminal',
  sex_offender: 'Sex Offender Registry',
  child_abuse: 'Child Abuse Registry',
  vulnerable_adult: 'Vulnerable Adult Registry',
  motor_vehicle: 'Motor Vehicle Record',
  reference_check: 'Reference Check',
};

const CHECK_STATUS_COLORS = {
  verified: 'text-emerald-700 bg-emerald-50',
  pending: 'text-amber-700 bg-amber-50',
  rejected: 'text-red-700 bg-red-50',
  expired: 'text-red-600 bg-red-50',
  not_required: 'text-gray-400 bg-gray-50',
};

const SimplePassportCard = ({ data, compact = false, isOwnProfile = false }) => {
  if (!data) return null;

  const clearanceLevel = data.clearance_level || null;
  const clearanceStatus = data.clearance_status || 'not_started';
  const clearanceColors = CLEARANCE_COLORS[clearanceLevel] || CLEARANCE_COLORS.level_1;
  const statusConfig = STATUS_CONFIG[clearanceStatus] || STATUS_CONFIG.not_started;
  const StatusIcon = statusConfig.icon;
  const bgChecks = (data.background_checks || []).filter(c => c.status !== 'not_required');
  const alertStatus = data.org_alert_status || data.flag_status || 'none';
  const alertOrgName = data.org_alert_org_name || data.flagged_by_org_name;

  return (
    <div className="card overflow-hidden" data-testid="simple-passport-card">
      {/* Header */}
      <div className="bg-[#8B2E3C] -mx-6 -mt-6 px-6 py-4 mb-5">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-white" />
          <span className="text-white font-bold text-lg">Volunteer Passport</span>
        </div>
        <div className="text-white/80 text-sm mt-1">{data.volunteer_name}</div>
      </div>

      {/* Org Alert Warning */}
      {alertStatus === 'active' && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="org-alert-warning">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            {isOwnProfile ? (
              <>
                <div className="font-semibold text-red-700 text-sm">Account Notice</div>
                <div className="text-xs text-red-600 mt-0.5">
                  There is a note on your record from a partner organization. If you have questions, please contact {alertOrgName ? alertOrgName : 'the organization'} or the program administrator.
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold text-red-700 text-sm">Active Organizational Alert</div>
                <div className="text-xs text-red-600 mt-0.5">
                  This participant has an active organizational alert. Please contact the reporting organization before assigning service.
                </div>
                {alertOrgName && (
                  <div className="text-xs text-red-500 mt-1">Alert by: {alertOrgName}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Clearance Level - Large Display */}
      <div className={`rounded-xl p-4 mb-4 ${clearanceColors.bg}`} data-testid="clearance-level-display">
        <div className="text-xs font-medium text-[#888] uppercase tracking-wide mb-1">Clearance Level</div>
        {clearanceLevel ? (
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-lg ${clearanceColors.accent}`}>
              {clearanceLevel === 'level_3' ? '3' : clearanceLevel === 'level_2' ? '2' : '1'}
            </div>
            <div>
              <div className={`font-bold text-lg ${clearanceColors.text}`}>
                {CLEARANCE_LABELS[clearanceLevel]}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig.color}`}>
                  {StatusIcon && <StatusIcon className="w-3 h-3 inline mr-1" />}
                  {statusConfig.label}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-[#888] text-sm">Not yet assigned</div>
        )}
      </div>

      {/* Verification Details */}
      {!compact && clearanceLevel && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-[#FBF4EA] rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-[#888] mb-1">
              <Building2 className="w-3.5 h-3.5" /> Verified By
            </div>
            <div className="text-sm font-medium text-[#222] truncate">
              {data.verified_by_org_name || '--'}
            </div>
          </div>
          <div className="bg-[#FBF4EA] rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-[#888] mb-1">
              <Calendar className="w-3.5 h-3.5" /> Verification Date
            </div>
            <div className="text-sm font-medium text-[#222]">
              {data.verification_date || '--'}
            </div>
          </div>
          <div className="bg-[#FBF4EA] rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-[#888] mb-1">
              <Calendar className="w-3.5 h-3.5" /> Expiration
            </div>
            <div className={`text-sm font-medium ${clearanceStatus === 'expired' ? 'text-red-600' : 'text-[#222]'}`}>
              {data.expiration_date || 'No expiration'}
            </div>
          </div>
          <div className="bg-[#FBF4EA] rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-[#888] mb-1">
              <Clock className="w-3.5 h-3.5" /> Approved Hours
            </div>
            <div className="text-sm font-medium text-[#222]">
              {(data.total_approved_hours || 0).toFixed(1)}h
            </div>
          </div>
        </div>
      )}

      {/* Background Check Verification Types */}
      {!compact && bgChecks.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-[#888] uppercase tracking-wide mb-2">Verification Types</div>
          <div className="space-y-1.5">
            {bgChecks.map(check => (
              <div key={check.check_type} className="flex items-center justify-between text-sm" data-testid={`passport-check-${check.check_type}`}>
                <span className="text-[#444]">{CHECK_TYPE_LABELS[check.check_type] || check.check_type}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHECK_STATUS_COLORS[check.status] || 'text-gray-500 bg-gray-50'}`}>
                  {check.status === 'verified' && <CheckCircle className="w-3 h-3 inline mr-0.5" />}
                  {check.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Volunteer Level */}
      <div className="flex items-center gap-3 py-3 border-t border-[#E9DDCF]">
        <Star className="w-4 h-4 text-[#C9A227]" />
        <div className="text-sm">
          <span className="text-[#888]">{isOwnProfile ? 'Volunteer Level: ' : 'Reputation: '}</span>
          <span className="font-medium text-[#222]">
            {LEVEL_LABELS[data.volunteer_level] || 'General Volunteer'}
          </span>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="pt-3 border-t border-[#E9DDCF]">
        <p className="text-[10px] text-[#aaa] italic leading-relaxed" data-testid="passport-disclaimer">
          Volunteer passport and clearance information is assigned and maintained by participating organizations. This platform does not store criminal history reports or detailed background check documents. Organizational alerts are provided only to prompt follow-up communication and should not be used as the sole basis for a placement decision.
        </p>
      </div>
    </div>
  );
};

export default SimplePassportCard;
